/**
 * DRP — Plan de reprise (disaster recovery runbook)
 */

import { useState, useEffect } from "react";

const BASE = import.meta.env.VITE_API_URL || "/api";

interface DrpStep {
  id: number;
  icon: string;
  title: string;
  detail: string;
  cmds: string;
  eta: string;
  deps: string;
  enabled: boolean;
  state: "pending" | "active" | "done" | "error";
}

export default function DRP() {
  const [steps, setSteps] = useState<DrpStep[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await fetch(`${BASE}/drp/steps`);
      if (res.ok) setSteps(await res.json());
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const executeStep = async (id: number) => {
    await fetch(`${BASE}/drp/steps/${id}/execute`, { method: "POST" });
    load();
  };

  const simulate = async () => {
    await fetch(`${BASE}/drp/simulate`, { method: "POST" });
    load();
  };

  const executeAll = async () => {
    await fetch(`${BASE}/drp/execute-all`, { method: "POST" });
    load();
  };

  const reset = async () => {
    await fetch(`${BASE}/drp/reset`, { method: "POST" });
    load();
  };

  const toggleStep = async (id: number, enabled: boolean) => {
    await fetch(`${BASE}/drp/steps/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: !enabled }),
    });
    load();
  };

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">disaster recovery</div>
          <h1>Plan de reprise</h1>
          <p>Runbook interactif étape par étape pour restaurer le cluster complet en cas de panne majeure.</p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={simulate}>🧪 Simuler</button>
          <button className="btn primary" onClick={executeAll}>⚡ Exécuter tout</button>
          <button className="btn" onClick={reset}>↻ Réinitialiser</button>
        </div>
      </div>

      {/* Restore point */}
      <div className="ops-card" style={{ marginBottom: "0" }}>
        <div className="timeline-header">
          <strong style={{ fontSize: "16px" }}>🎯 Restore point sélectionné</strong>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px" }}>
          <div><div style={{ fontFamily: "var(--mono)", fontSize: "10px", textTransform: "uppercase", color: "var(--muted)" }}>Source</div><div style={{ fontSize: "14px", fontWeight: 600, color: "var(--olive)" }}>☁️ Wasabi S3 · synapse-backups</div></div>
          <div><div style={{ fontFamily: "var(--mono)", fontSize: "10px", textTransform: "uppercase", color: "var(--muted)" }}>Timestamp</div><div style={{ fontSize: "14px", fontWeight: 600 }}>2026-07-24 02:00 UTC</div></div>
          <div><div style={{ fontFamily: "var(--mono)", fontSize: "10px", textTransform: "uppercase", color: "var(--muted)" }}>Cible</div><div style={{ fontSize: "14px", fontWeight: 600 }}>🖥️ Cluster k3s · 3 nœuds</div></div>
        </div>
      </div>

      {/* Steps */}
      {loading && <div className="no-results">Chargement…</div>}

      {!loading && steps.map((step, i) => (
        <div key={step.id} className="ops-card" style={{ opacity: step.enabled ? 1 : 0.4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <div style={{
              width: "32px", height: "32px", borderRadius: "10px",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "14px", fontWeight: 700, fontFamily: "var(--mono)",
              background: step.state === "done" ? "rgba(94,199,168,0.16)" : step.state === "active" ? "rgba(232,184,90,0.16)" : "rgba(255,255,255,0.04)",
              color: step.state === "done" ? "#b8eee0" : step.state === "active" ? "#ffe8b8" : "var(--muted)",
            }}>
              {step.state === "done" ? "✓" : i + 1}
            </div>
            <span style={{ fontSize: "18px" }}>{step.icon}</span>
            <strong style={{ fontSize: "15px" }}>{step.title}</strong>
            <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
              <button className="btn" style={{ fontSize: "10px" }} onClick={() => toggleStep(step.id, step.enabled)}>
                {step.enabled ? "on" : "off"}
              </button>
              {step.state !== "done" && step.enabled && (
                <button className="btn primary" style={{ fontSize: "10px" }} onClick={() => executeStep(step.id)}>✓ Exécuter</button>
              )}
            </div>
          </div>
          <div style={{ fontSize: "13px", color: "var(--muted)", lineHeight: 1.4 }}>{step.detail}</div>
          {step.enabled && (
            <>
              <div style={{ display: "flex", gap: "12px", fontSize: "11px", marginTop: "8px" }}>
                <span style={{ color: "var(--muted)" }}>⏱ <strong style={{ color: "var(--text)" }}>{step.eta}</strong></span>
                <span style={{ color: "var(--muted)" }}>🔗 <strong style={{ color: "var(--text)" }}>{step.deps}</strong></span>
              </div>
              <pre style={{ marginTop: "8px", padding: "10px", borderRadius: "10px", background: "rgba(0,0,0,0.2)", fontSize: "11px", fontFamily: "var(--mono)", color: "var(--cyan)", overflow: "auto", whiteSpace: "pre-wrap" }}>
                {step.cmds}
              </pre>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
